import React, { useCallback, useState, useRef, useEffect } from 'react';
import ReactFlow, {
  addEdge,
  Connection,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  BackgroundVariant,
  ReactFlowProvider,
  useViewport,
  SelectionMode,
} from 'reactflow';
import styled from 'styled-components';
import 'reactflow/dist/style.css';

import { CircuitSchema, CircuitNode, CircuitEdge, ComponentType, SimulationResult } from '../types';
import { createAnnotationId, createEdgeId, createNodeId } from '../services/ids';
import { HydraulicNodeComponent } from './HydraulicNode';
import { HydraulicEdgeComponent } from './HydraulicEdge';
import { AnnotationNode } from './Annotation';
import { canConnect, findPort, getPortLayout, getPorts } from '../services/ports';
import { newNodeData } from '../services/componentDefaults';

const HydraulicNodeComponentWithProps = React.memo((props: any) => {
  return <HydraulicNodeComponent {...props} />;
});

const nodeTypes = {
  hydraulicNode: HydraulicNodeComponentWithProps,
  annotationNode: AnnotationNode,
};

const getNodeTypes = () => nodeTypes;

const HydraulicEdgeComponentWithProps = React.memo((props: any) => {
  const { onControlPointChange, onLabelChange, reactFlowInstance, autoEditLabel, edgeId, onAutoEditComplete, simulationFlowRate, ...edgeProps } = props;
  const shouldAutoEdit = autoEditLabel?.edgeId === edgeId ? autoEditLabel.labelIndex : null;

  return (
    <HydraulicEdgeComponent
      {...edgeProps}
      onControlPointChange={onControlPointChange}
      controlPoints={edgeProps.data?.controlPoints || []}
      labels={edgeProps.data?.labels || []}
      onLabelChange={onLabelChange}
      onAutoEditComplete={onAutoEditComplete}
      autoEditLabelIndex={shouldAutoEdit}
      reactFlowInstance={reactFlowInstance}
      simulationFlowRate={simulationFlowRate}
    />
  );
});

interface FlowCanvasProps {
  schema: CircuitSchema;
  onSchemaChange: (schema: CircuitSchema) => void;
  onNodeSelect: (nodeId: string | null) => void;
  onElementSelect: (type: 'node' | 'edge', id: string | null) => void;
  onMultiSelect?: (nodeIds: string[], edgeIds: string[]) => void;
  selectedElements: { nodes: string[]; edges: string[] };
  onControlPointChange?: (edgeId: string, controlPoints: { x: number; y: number }[]) => void;
  activeSimResult?: SimulationResult | null;
  addAnnotationRequest?: { type: 'label' | 'comment'; id: number } | null;
  onNodeDoubleClick?: (nodeId: string) => void;
  readOnly?: boolean;
  /** Bump to fit the whole circuit into view (e.g. after the assistant builds one). */
  fitViewRequest?: number;
}

const FlowContainer = styled.div`
  width: 100%;
  height: 100%;

  .react-flow__node {
    border-radius: 6px;
  }

  .react-flow__node.selected {
    box-shadow: 0 0 0 2px #1a73e8;
  }

  .react-flow__edge.selected .react-flow__edge-path {
    stroke: #1a73e8;
    stroke-width: 3px;
  }

  .react-flow__edge .react-flow__edge-path {
    stroke: #5f6368;
    stroke-width: 2px;
  }
`;

interface ConnectionDraft {
  sourceNodeId: string;
  sourceHandleId: string;
  sourceHandleType: 'source' | 'target';
  sourcePos: { x: number; y: number };
  waypoints: { x: number; y: number }[];
}

function snapToAxis(
  point: { x: number; y: number },
  ref: { x: number; y: number },
  threshold = 5
): { x: number; y: number } {
  const dx = point.x - ref.x;
  const dy = point.y - ref.y;
  const angle = Math.abs(Math.atan2(dy, dx) * 180 / Math.PI);

  if (angle < threshold || angle > 180 - threshold) {
    return { x: point.x, y: ref.y };
  }
  if (Math.abs(angle - 90) < threshold) {
    return { x: ref.x, y: point.y };
  }
  return point;
}

const ConnectionModeBar = styled.div`
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  background: #1a73e8;
  color: white;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(26, 115, 232, 0.4);
  pointer-events: none;
`;

const FlowCanvasContent: React.FC<FlowCanvasProps> = ({
  schema,
  onSchemaChange,
  onNodeSelect,
  onElementSelect,
  onMultiSelect,
  selectedElements,
  onControlPointChange,
  activeSimResult,
  addAnnotationRequest,
  onNodeDoubleClick,
  readOnly,
  fitViewRequest,
}) => {
  const [reactFlowInstance, setReactFlowInstance] = useState<any>(null);

  useEffect(() => {
    if (!fitViewRequest || !reactFlowInstance) return;
    // Wait a frame so React Flow has measured the new nodes.
    const timer = setTimeout(() => reactFlowInstance.fitView({ padding: 0.15, duration: 300 }), 80);
    return () => clearTimeout(timer);
  }, [fitViewRequest, reactFlowInstance]);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const viewport = useViewport();

  const [nodes, setNodes, onNodesChangeBase] = useNodesState(schema.nodes);
  const [edges, setEdges, onEdgesChangeBase] = useEdgesState(schema.edges);

  // Ref to always access the latest schema (avoids stale closures in setTimeout callbacks)
  const schemaRef = useRef(schema);
  schemaRef.current = schema;
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;

  const componentOf = (nodeId: string) =>
    nodesRef.current.find(n => n.id === nodeId)?.data?.component;

  // Shift-key tracking for rectangle selection mode
  const [shiftPressed, setShiftPressed] = useState(false);
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Shift') setShiftPressed(true); };
    const onKeyUp = (e: KeyboardEvent) => { if (e.key === 'Shift') setShiftPressed(false); };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  // Click-to-place connection mode state
  const [connectionDraft, setConnectionDraft] = useState<ConnectionDraft | null>(null);
  const [mousePos, setMousePos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const connectStartRef = useRef<{ nodeId: string; handleId: string; handleType: string } | null>(null);
  const connectSucceededRef = useRef(false);
  const pendingTargetRef = useRef<{ nodeId: string; handleId: string; handleType: string } | null>(null);

  const onNodesChange = useCallback((changes: any[]) => {
    const filtered = readOnly ? changes.filter((c: any) => c.type === 'select') : changes;
    if (filtered.length === 0) return;
    onNodesChangeBase(filtered);
    if (!readOnly) {
      setTimeout(() => {
        setNodes(currentNodes => {
          setEdges(currentEdges => {
            onSchemaChange({
              ...schemaRef.current,
              nodes: currentNodes as CircuitNode[],
              edges: currentEdges as CircuitEdge[],
              viewport: reactFlowInstance?.getViewport(),
            });
            return currentEdges;
          });
          return currentNodes;
        });
      }, 0);
    }
  }, [onNodesChangeBase, setNodes, setEdges, reactFlowInstance, onSchemaChange, readOnly]);

  const onEdgesChange = useCallback((changes: any[]) => {
    const filtered = readOnly ? changes.filter((c: any) => c.type === 'select') : changes;
    if (filtered.length === 0) return;
    onEdgesChangeBase(filtered);
    if (!readOnly) {
      setTimeout(() => {
        setNodes(currentNodes => {
          setEdges(currentEdges => {
            onSchemaChange({
              ...schemaRef.current,
              nodes: currentNodes as CircuitNode[],
              edges: currentEdges as CircuitEdge[],
              viewport: reactFlowInstance?.getViewport(),
            });
            return currentEdges;
          });
          return currentNodes;
        });
      }, 0);
    }
  }, [onEdgesChangeBase, setNodes, setEdges, reactFlowInstance, onSchemaChange, readOnly]);

  const handleControlPointChange = useCallback((edgeId: string, controlPoints: { x: number; y: number }[]) => {
    setEdges((eds) =>
      eds.map((edge) =>
        edge.id === edgeId
          ? { ...edge, data: { ...edge.data, controlPoints } }
          : edge
      )
    );

    setTimeout(() => {
      setNodes(currentNodes => {
        setEdges(currentEdges => {
          const updatedEdges = currentEdges.map((edge) =>
            edge.id === edgeId
              ? { ...edge, data: { ...edge.data, controlPoints } }
              : edge
          );
          onSchemaChange({
            ...schemaRef.current,
            nodes: currentNodes as CircuitNode[],
            edges: updatedEdges as CircuitEdge[],
            viewport: reactFlowInstance?.getViewport(),
          });
          return updatedEdges;
        });
        return currentNodes;
      });
    }, 0);
  }, [setEdges, setNodes, onSchemaChange, reactFlowInstance]);

  const handleLabelChange = useCallback((edgeId: string, labels: { text: string; offset: number }[]) => {
    setEdges((eds) =>
      eds.map((edge) =>
        edge.id === edgeId
          ? { ...edge, data: { ...edge.data, labels } }
          : edge
      )
    );

    setTimeout(() => {
      setNodes(currentNodes => {
        setEdges(currentEdges => {
          const updatedEdges = currentEdges.map((edge) =>
            edge.id === edgeId
              ? { ...edge, data: { ...edge.data, labels } }
              : edge
          );
          onSchemaChange({
            ...schemaRef.current,
            nodes: currentNodes as CircuitNode[],
            edges: updatedEdges as CircuitEdge[],
            viewport: reactFlowInstance?.getViewport(),
          });
          return updatedEdges;
        });
        return currentNodes;
      });
    }, 0);
  }, [setEdges, setNodes, onSchemaChange, reactFlowInstance]);

  const edgeClickTimes = React.useRef<Map<string, number>>(new Map());
  const [autoEditLabel, setAutoEditLabel] = React.useState<{ edgeId: string; labelIndex: number } | null>(null);

  const handleAutoEditComplete = React.useCallback(() => {
    setAutoEditLabel(null);
  }, []);

  const handleAnnotationTextChange = useCallback((nodeId: string, text: string) => {
    setNodes(nds => nds.map(n =>
      n.id === nodeId ? { ...n, data: { ...n.data, text } } : n
    ));
    setTimeout(() => {
      setNodes(currentNodes => {
        setEdges(currentEdges => {
          onSchemaChange({
            ...schemaRef.current,
            nodes: currentNodes as CircuitNode[],
            edges: currentEdges as CircuitEdge[],
            viewport: reactFlowInstance?.getViewport(),
          });
          return currentEdges;
        });
        return currentNodes;
      });
    }, 0);
  }, [setNodes, setEdges, reactFlowInstance, onSchemaChange]);

  const handleAnnotationSizeChange = useCallback((nodeId: string, width: number, height: number) => {
    setNodes(nds => nds.map(n =>
      n.id === nodeId ? { ...n, data: { ...n.data, width, height } } : n
    ));
    setTimeout(() => {
      setNodes(currentNodes => {
        setEdges(currentEdges => {
          onSchemaChange({
            ...schemaRef.current,
            nodes: currentNodes as CircuitNode[],
            edges: currentEdges as CircuitEdge[],
            viewport: reactFlowInstance?.getViewport(),
          });
          return currentEdges;
        });
        return currentNodes;
      });
    }, 0);
  }, [setNodes, setEdges, reactFlowInstance, onSchemaChange]);

  const handleAddAnnotation = useCallback((type: 'label' | 'comment') => {
    const center = reactFlowInstance?.screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    }) || { x: 200, y: 200 };

    const newNode: any = {
      id: createAnnotationId(),
      type: 'annotationNode',
      position: center,
      data: {
        text: type === 'label' ? 'Label' : 'Comment',
        annotationType: type,
        onTextChange: handleAnnotationTextChange,
        onSizeChange: handleAnnotationSizeChange,
      },
    };

    setNodes(nds => nds.concat(newNode));
    setTimeout(() => {
      setNodes(currentNodes => {
        setEdges(currentEdges => {
          onSchemaChange({
            ...schemaRef.current,
            nodes: currentNodes as CircuitNode[],
            edges: currentEdges as CircuitEdge[],
            viewport: reactFlowInstance?.getViewport(),
          });
          return currentEdges;
        });
        return currentNodes;
      });
    }, 0);
  }, [reactFlowInstance, setNodes, setEdges, onSchemaChange, handleAnnotationTextChange, handleAnnotationSizeChange]);

  // Respond to annotation requests from TopBar
  const prevAnnotationReqId = useRef<number | null>(null);
  useEffect(() => {
    if (addAnnotationRequest && addAnnotationRequest.id !== prevAnnotationReqId.current) {
      prevAnnotationReqId.current = addAnnotationRequest.id;
      handleAddAnnotation(addAnnotationRequest.type);
    }
  }, [addAnnotationRequest, handleAddAnnotation]);

  const prevSchemaNodes = React.useRef(schema.nodes);
  const prevSchemaEdges = React.useRef(schema.edges);

  React.useEffect(() => {
    const nodesChanged = JSON.stringify(schema.nodes) !== JSON.stringify(prevSchemaNodes.current);
    const edgesChanged = JSON.stringify(schema.edges) !== JSON.stringify(prevSchemaEdges.current);

    if (nodesChanged) {
      // Inject callbacks for annotation nodes
      const nodesWithCallbacks = schema.nodes.map(n =>
        n.type === 'annotationNode'
          ? { ...n, data: { ...n.data, onTextChange: handleAnnotationTextChange, onSizeChange: handleAnnotationSizeChange } }
          : n
      );
      setNodes(nodesWithCallbacks);
      prevSchemaNodes.current = schema.nodes;
    }
    if (edgesChanged) {
      setEdges(schema.edges);
      prevSchemaEdges.current = schema.edges;
    }
  }, [schema.nodes, schema.edges, setNodes, setEdges, handleAnnotationTextChange, handleAnnotationSizeChange]);

  // Inject simulation results into node data when results change
  React.useEffect(() => {
    if (!activeSimResult) {
      // Clear simulation results from nodes
      setNodes(nds => nds.map(n => {
        if (n.data?.simulationResult) {
          const { simulationResult, ...rest } = n.data;
          return { ...n, data: rest };
        }
        return n;
      }));
      return;
    }
    setNodes(nds => nds.map(n => {
      const compResult = activeSimResult.components[n.id];
      if (compResult) {
        return { ...n, data: { ...n.data, simulationResult: compResult } };
      }
      const { simulationResult, ...rest } = n.data || {};
      return { ...n, data: rest };
    }));
  }, [activeSimResult, setNodes]);

  // Calculate handle position in flow coordinates
  const getHandlePosition = useCallback((nodeId: string, handleId: string, handleType: string) => {
    if (!reactFlowInstance) return { x: 0, y: 0 };
    const node = reactFlowInstance.getNode(nodeId);
    if (!node) return { x: 0, y: 0 };

    const nodeWidth = node.width || 110;
    const nodeHeight = node.height || 80;

    const isInput = handleType === 'target';
    const layout = getPortLayout(node.data?.component).find(p => p.id === handleId);
    const yPercent = layout ? layout.yPercent : 50;

    const x = isInput
      ? node.position.x
      : node.position.x + nodeWidth;
    const y = node.position.y + (nodeHeight * yPercent / 100);

    return { x, y };
  }, [reactFlowInstance]);

  // Find the best compatible port on a node for completing a draft connection.
  // Returns { handleId, handleType, position } or null if no compatible port.
  const findBestPort = useCallback((
    nodeId: string,
    needType: 'source' | 'target',
    clickPos: { x: number; y: number },
    requiredDomain?: string,
  ): { handleId: string; handleType: string; position: { x: number; y: number } } | null => {
    if (!reactFlowInstance) return null;
    const node = reactFlowInstance.getNode(nodeId);
    if (!node) return null;

    const component = node.data?.component;
    if (!component) return null;

    const direction = needType === 'target' ? 'in' : 'out';
    const candidates = getPorts(component).filter(p =>
      p.direction === direction && (!requiredDomain || p.domain === requiredDomain)
    );
    if (candidates.length === 0) return null;

    let bestDist = Infinity;
    let best: { handleId: string; handleType: string; position: { x: number; y: number } } | null = null;

    for (const port of candidates) {
      const handleId = port.id;
      const pos = getHandlePosition(nodeId, handleId, needType);
      const dx = pos.x - clickPos.x;
      const dy = pos.y - clickPos.y;
      const dist = dx * dx + dy * dy;
      if (dist < bestDist) {
        bestDist = dist;
        best = { handleId, handleType: needType, position: pos };
      }
    }

    return best;
  }, [reactFlowInstance, getHandlePosition]);

  // Create edge with waypoints from draft
  const createEdgeFromDraft = useCallback((
    draft: ConnectionDraft,
    targetNodeId: string,
    targetHandleId: string,
    targetHandleType: string,
  ) => {
    // A component cannot feed itself.
    if (draft.sourceNodeId === targetNodeId) return;

    // Determine source/target based on handle types
    let source: string, sourceHandle: string, target: string, targetHandle: string;
    let waypoints = [...draft.waypoints];

    if (draft.sourceHandleType === 'source' && targetHandleType === 'target') {
      source = draft.sourceNodeId;
      sourceHandle = draft.sourceHandleId;
      target = targetNodeId;
      targetHandle = targetHandleId;
    } else if (draft.sourceHandleType === 'target' && targetHandleType === 'source') {
      source = targetNodeId;
      sourceHandle = targetHandleId;
      target = draft.sourceNodeId;
      targetHandle = draft.sourceHandleId;
      waypoints.reverse();
    } else {
      // Same type — can't connect
      return;
    }

    // Liquid and gas ports cannot be joined.
    if (!canConnect(componentOf(source), sourceHandle, componentOf(target), targetHandle)) return;

    const newEdge: CircuitEdge = {
      id: createEdgeId(),
      source,
      target,
      sourceHandle,
      targetHandle,
      type: 'hydraulicEdge',
      data: { controlPoints: waypoints },
    };

    setEdges((eds) => addEdge(newEdge, eds));
    setTimeout(() => {
      setNodes(currentNodes => {
        setEdges(currentEdges => {
          onSchemaChange({
            ...schemaRef.current,
            nodes: currentNodes as CircuitNode[],
            edges: currentEdges as CircuitEdge[],
            viewport: reactFlowInstance?.getViewport(),
          });
          return currentEdges;
        });
        return currentNodes;
      });
    }, 0);
  }, [setEdges, setNodes, reactFlowInstance, onSchemaChange]);

  /**
   * Rejects self-loops (an output wired back into the same component's input
   * is not a circuit, and the solver has no meaning for it) and edges joining
   * ports of different media (a liquid outlet into a gas inlet).
   */
  const isValidConnection = useCallback((connection: Connection) => {
    if (!connection.source || !connection.target) return false;
    if (connection.source === connection.target) return false;
    return canConnect(
      componentOf(connection.source), connection.sourceHandle,
      componentOf(connection.target), connection.targetHandle,
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onConnect = useCallback(
    (params: Connection) => {
      if (!isValidConnection(params)) return;

      if (connectionDraft) {
        // Complete draft connection with waypoints
        const clickedPort = getPorts(componentOf(params.target!)).find(p => p.id === params.targetHandle);
        const targetHandleType = clickedPort?.direction === 'out' ? 'source' : 'target';
        createEdgeFromDraft(
          connectionDraft,
          params.target!,
          params.targetHandle!,
          targetHandleType,
        );
        setConnectionDraft(null);
        connectSucceededRef.current = true;
        connectStartRef.current = null;
        pendingTargetRef.current = null;
        return;
      }

      connectSucceededRef.current = true;

      const newEdge: CircuitEdge = {
        id: createEdgeId(),
        source: params.source!,
        target: params.target!,
        sourceHandle: params.sourceHandle!,
        targetHandle: params.targetHandle!,
        type: 'hydraulicEdge',
        data: { controlPoints: [] },
      };

      setEdges((eds) => addEdge(newEdge, eds));
      setTimeout(() => {
        setNodes(currentNodes => {
          setEdges(currentEdges => {
            onSchemaChange({
              ...schemaRef.current,
              nodes: currentNodes as CircuitNode[],
              edges: currentEdges as CircuitEdge[],
              viewport: reactFlowInstance?.getViewport(),
            });
            return currentEdges;
          });
          return currentNodes;
        });
      }, 0);
    },
    [setEdges, setNodes, reactFlowInstance, onSchemaChange, connectionDraft, createEdgeFromDraft, isValidConnection]
  );

  // onConnectStart: record handle info
  const onConnectStart = useCallback((_event: any, params: any) => {
    const info = {
      nodeId: params.nodeId || '',
      handleId: params.handleId || '',
      handleType: params.handleType || 'source',
    };

    if (connectionDraft) {
      // We're in draft mode — this is a potential target
      pendingTargetRef.current = info;
    } else {
      connectStartRef.current = info;
      connectSucceededRef.current = false;
    }
  }, [connectionDraft]);

  // onConnectEnd: start draft if drag didn't complete, or complete draft if target pending
  const onConnectEnd = useCallback(() => {
    if (connectionDraft && pendingTargetRef.current) {
      // Complete the draft connection
      const target = pendingTargetRef.current;
      createEdgeFromDraft(connectionDraft, target.nodeId, target.handleId, target.handleType);
      setConnectionDraft(null);
      connectStartRef.current = null;
      pendingTargetRef.current = null;
      connectSucceededRef.current = false;
      return;
    }

    if (!connectSucceededRef.current && !connectionDraft && connectStartRef.current) {
      // Drag didn't complete a connection — start draft mode
      const { nodeId, handleId, handleType } = connectStartRef.current;
      const sourcePos = getHandlePosition(nodeId, handleId, handleType);

      setConnectionDraft({
        sourceNodeId: nodeId,
        sourceHandleId: handleId,
        sourceHandleType: handleType as 'source' | 'target',
        sourcePos,
        waypoints: [],
      });
    }

    connectStartRef.current = null;
    pendingTargetRef.current = null;
    connectSucceededRef.current = false;
  }, [connectionDraft, createEdgeFromDraft, getHandlePosition]);

  // Cancel connection draft
  const cancelDraft = useCallback(() => {
    setConnectionDraft(null);
    connectStartRef.current = null;
    pendingTargetRef.current = null;
  }, []);

  // Escape key cancels draft
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && connectionDraft) {
        cancelDraft();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [connectionDraft, cancelDraft]);

  // Mouse move handler for preview line
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (connectionDraft && reactFlowInstance) {
      const flowPos = reactFlowInstance.screenToFlowPosition({
        x: e.clientX,
        y: e.clientY,
      });
      setMousePos(flowPos);
    }
  }, [connectionDraft, reactFlowInstance]);

  // Right-click cancels draft
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    if (connectionDraft) {
      e.preventDefault();
      cancelDraft();
    }
  }, [connectionDraft, cancelDraft]);

  // Build draft preview path — no snap on the live cursor segment so the line
  // can freely reach ports at any angle; snap only applies when placing waypoints.
  const draftPathData = React.useMemo(() => {
    if (!connectionDraft) return '';
    const allPoints = [connectionDraft.sourcePos, ...connectionDraft.waypoints, mousePos];
    if (allPoints.length < 2) return '';
    let d = `M ${allPoints[0].x} ${allPoints[0].y}`;
    for (let i = 1; i < allPoints.length; i++) {
      d += ` L ${allPoints[i].x} ${allPoints[i].y}`;
    }
    return d;
  }, [connectionDraft, mousePos]);

  React.useEffect(() => {
    setNodes((nds) =>
      nds.map((node) => ({
        ...node,
        selected: selectedElements.nodes.includes(node.id),
      }))
    );
    setEdges((eds) =>
      eds.map((edge) => ({
        ...edge,
        selected: selectedElements.edges.includes(edge.id),
      }))
    );
  }, [selectedElements, setNodes, setEdges]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();

    if (readOnly || !wrapperRef.current || !reactFlowInstance) return;

    // Handle annotation drops
    const annotationType = e.dataTransfer.getData('application/annotation');
    if (annotationType === 'label' || annotationType === 'comment') {
      const position = reactFlowInstance.screenToFlowPosition({
        x: e.clientX,
        y: e.clientY,
      });

      const newNode: any = {
        id: createAnnotationId(),
        type: 'annotationNode',
        position,
        data: {
          text: annotationType === 'label' ? 'Label' : 'Comment',
          annotationType,
          onTextChange: handleAnnotationTextChange,
          onSizeChange: handleAnnotationSizeChange,
        },
      };

      setNodes(nds => nds.concat(newNode));
      setTimeout(() => {
        setNodes(currentNodes => {
          setEdges(currentEdges => {
            onSchemaChange({
              ...schemaRef.current,
              nodes: currentNodes as CircuitNode[],
              edges: currentEdges as CircuitEdge[],
              viewport: reactFlowInstance?.getViewport(),
            });
            return currentEdges;
          });
          return currentNodes;
        });
      }, 0);
      return;
    }

    try {
      const elementData = e.dataTransfer.getData('application/json');
      if (!elementData) return;

      const component: ComponentType = JSON.parse(elementData);
      const position = reactFlowInstance.screenToFlowPosition({
        x: e.clientX,
        y: e.clientY,
      });

      // Offset so the node center lands at the cursor, not the top-left corner
      const estimatedWidth = 130;
      const maxPorts = Math.max(component.input_ports, component.output_ports);
      const estimatedHeight = maxPorts > 1 ? Math.max(80, maxPorts * 30) : 80;
      position.x -= estimatedWidth / 2;
      position.y -= estimatedHeight / 2;

      const newNode: CircuitNode = {
        id: createNodeId(),
        type: 'hydraulicNode',
        position,
        data: newNodeData(component),
      };

      setNodes((nds) => nds.concat(newNode));

      // Notify parent of schema change
      setTimeout(() => {
        setNodes(currentNodes => {
          setEdges(currentEdges => {
            onSchemaChange({
              ...schemaRef.current,
              nodes: currentNodes as CircuitNode[],
              edges: currentEdges as CircuitEdge[],
              viewport: reactFlowInstance?.getViewport(),
            });
            return currentEdges;
          });
          return currentNodes;
        });
      }, 0);
    } catch (error) {
      console.error('Error handling drop:', error);
    }
  }, [reactFlowInstance, setNodes, setEdges, onSchemaChange, handleAnnotationTextChange, handleAnnotationSizeChange, readOnly]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (readOnly) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, [readOnly]);

  const edgeTypes = React.useMemo(() => ({
    hydraulicEdge: (edgeProps: any) => {
      // Get flow rate from edge simulation result (accounts for branch splits)
      let simulationFlowRate: number | undefined;
      if (activeSimResult && edgeProps.id) {
        const edgeResult = activeSimResult.edges?.[edgeProps.id];
        if (edgeResult) {
          simulationFlowRate = edgeResult.flow_rate;
        }
      }
      return (
        <HydraulicEdgeComponentWithProps
          {...edgeProps}
          edgeId={edgeProps.id}
          reactFlowInstance={reactFlowInstance}
          onControlPointChange={handleControlPointChange}
          onLabelChange={handleLabelChange}
          onAutoEditComplete={handleAutoEditComplete}
          autoEditLabel={autoEditLabel}
          simulationFlowRate={simulationFlowRate}
        />
      );
    },
  }), [reactFlowInstance, handleControlPointChange, handleLabelChange, handleAutoEditComplete, autoEditLabel, activeSimResult]);

  return (
    <FlowContainer
      ref={wrapperRef}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onMouseMove={handleMouseMove}
      onContextMenu={handleContextMenu}
      style={connectionDraft ? { cursor: 'crosshair' } : undefined}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        isValidConnection={isValidConnection}
        onConnectStart={onConnectStart}
        onConnectEnd={onConnectEnd}
        onNodeDoubleClick={(_event, node) => {
          // Only fire for hydraulic nodes, not annotations
          if (node.type === 'annotationNode') return;
          onNodeDoubleClick?.(node.id);
        }}
        onNodeClick={(event, node) => {
          // If in connection draft mode, auto-connect to the best compatible port
          if (connectionDraft && reactFlowInstance) {
            // Don't connect to the same node
            if (node.id === connectionDraft.sourceNodeId) return;

            const clickPos = reactFlowInstance.screenToFlowPosition({
              x: event.clientX,
              y: event.clientY,
            });

            // Determine what port type we need on the target
            const needType = connectionDraft.sourceHandleType === 'source' ? 'target' : 'source';
            const draftPort = findPort(
              componentOf(connectionDraft.sourceNodeId),
              connectionDraft.sourceHandleId,
              connectionDraft.sourceHandleType === 'source' ? 'out' : 'in',
            );
            const bestPort = findBestPort(node.id, needType, clickPos, draftPort?.domain);

            if (bestPort) {
              createEdgeFromDraft(connectionDraft, node.id, bestPort.handleId, bestPort.handleType);
              setConnectionDraft(null);
              connectStartRef.current = null;
              pendingTargetRef.current = null;
            }
            return;
          }

          setNodes((nds) =>
            nds.map((n) => ({ ...n, selected: n.id === node.id }))
          );
          setEdges((eds) => eds.map((edge) => ({ ...edge, selected: false })));
          onElementSelect('node', node.id);
        }}
        onEdgeClick={(event, edge) => {
          const now = Date.now();
          const lastClickTime = edgeClickTimes.current.get(edge.id);

          if (lastClickTime && (now - lastClickTime) < 300) {
            if (reactFlowInstance) {
              const currentLabels = edge.data?.labels || [];
              const newLabelIndex = currentLabels.length;
              handleLabelChange(edge.id, [
                ...currentLabels,
                { text: '', offset: 0.5 },
              ]);
              setAutoEditLabel({ edgeId: edge.id, labelIndex: newLabelIndex });
            }
            edgeClickTimes.current.delete(edge.id);
          } else {
            edgeClickTimes.current.set(edge.id, now);
            setEdges((eds) =>
              eds.map((e) => ({ ...e, selected: e.id === edge.id }))
            );
            setNodes((nds) => nds.map((node) => ({ ...node, selected: false })));
            onElementSelect('edge', edge.id);
          }
        }}
        onPaneClick={(event) => {
          if (connectionDraft && reactFlowInstance) {
            const flowPos = reactFlowInstance.screenToFlowPosition({
              x: event.clientX,
              y: event.clientY,
            });
            const lastPoint = connectionDraft.waypoints.length > 0
              ? connectionDraft.waypoints[connectionDraft.waypoints.length - 1]
              : connectionDraft.sourcePos;
            const snapped = snapToAxis(flowPos, lastPoint);
            setConnectionDraft({
              ...connectionDraft,
              waypoints: [...connectionDraft.waypoints, snapped],
            });
            return;
          }
          setNodes((nds) => nds.map((node) => ({ ...node, selected: false })));
          setEdges((eds) => eds.map((edge) => ({ ...edge, selected: false })));
          onElementSelect('node', null);
        }}
        onInit={setReactFlowInstance}
        onSelectionChange={({ nodes: selNodes, edges: selEdges }) => {
          if (selNodes.length + selEdges.length > 1) {
            onMultiSelect?.(
              selNodes.map(n => n.id),
              selEdges.map(e => e.id),
            );
          }
        }}
        nodeTypes={getNodeTypes()}
        edgeTypes={edgeTypes}
        defaultViewport={{ x: 0, y: 0, zoom: 1 }}
        attributionPosition="top-right"
        selectionOnDrag={shiftPressed && !connectionDraft}
        panOnDrag={!shiftPressed || !!connectionDraft}
        selectionMode={SelectionMode.Partial}
        nodesDraggable={!readOnly}
        nodesConnectable={!readOnly}
        elementsSelectable
      >
        <Controls />
        <Background variant={BackgroundVariant.Dots} gap={12} size={1} />

        {/* Draft connection preview */}
        {connectionDraft && draftPathData && (
          <svg
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              zIndex: 1000,
            }}
          >
            <g transform={`translate(${viewport.x},${viewport.y}) scale(${viewport.zoom})`}>
              <path
                d={draftPathData}
                stroke="#1a73e8"
                strokeWidth={2 / viewport.zoom}
                strokeDasharray={`${6 / viewport.zoom} ${4 / viewport.zoom}`}
                fill="none"
              />
              {connectionDraft.waypoints.map((wp, i) => (
                <circle
                  key={i}
                  cx={wp.x}
                  cy={wp.y}
                  r={4 / viewport.zoom}
                  fill="#1a73e8"
                />
              ))}
            </g>
          </svg>
        )}
      </ReactFlow>

      {connectionDraft && (
        <ConnectionModeBar>
          Click to add waypoints &middot; Click a port to finish &middot; Esc / Right-click to cancel
        </ConnectionModeBar>
      )}
    </FlowContainer>
  );
};

export const FlowCanvas: React.FC<FlowCanvasProps> = (props) => {
  return (
    <ReactFlowProvider>
      <FlowCanvasContent {...props} />
    </ReactFlowProvider>
  );
};
